/**
 * @fileoverview Admin Dashboard Hooks
 *
 * Custom React hooks for fetching admin dashboard data using React Query.
 * These hooks connect to Firebase Functions and provide caching,
 * loading states, and error handling.
 *
 * @version 1.0.0
 * @created 2025-12-11
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// =============================================================================
// DASHBOARD STATS
// =============================================================================

/**
 * Hook to fetch admin dashboard statistics
 * Returns project counts by type and status
 *
 * @returns {Object} Query result with stats data
 */
export function useAdminDashboardStats({ showTestAccounts } = {}) {
  return useQuery({
    queryKey: ['adminDashboardStats', showTestAccounts],
    queryFn: async () => {
      const getStats = httpsCallable(functions, 'getAdminDashboardStats');
      const result = await getStats({ showTestAccounts });
      
      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to fetch dashboard stats');
      }
      
      return result.data.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// ALERTS
// =============================================================================

/**
 * Hook to fetch admin dashboard alerts
 * Returns overdue tasks, missing selections, stalled projects, pending COs
 *
 * @returns {Object} Query result with alerts data
 */
export function useAdminAlerts({ showTestAccounts } = {}) {
  return useQuery({
    queryKey: ['adminAlerts', showTestAccounts],
    queryFn: async () => {
      const getAlerts = httpsCallable(functions, 'getAdminAlerts');
      const result = await getAlerts({ showTestAccounts });
      
      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to fetch alerts');
      }
      
      return result.data.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// DEADLINES
// =============================================================================

/**
 * Hook to fetch upcoming deadlines
 *
 * @param {number} daysAhead - Number of days to look ahead (default 30)
 * @returns {Object} Query result with deadlines data
 */
export function useUpcomingDeadlines(daysAhead = 30, { showTestAccounts } = {}) {
  return useQuery({
    queryKey: ['upcomingDeadlines', daysAhead, showTestAccounts],
    queryFn: async () => {
      const getDeadlines = httpsCallable(functions, 'getUpcomingDeadlines');
      const result = await getDeadlines({ daysAhead, showTestAccounts });
      
      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to fetch deadlines');
      }
      
      return result.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =============================================================================
// APPROVALS
// =============================================================================

/**
 * Hook to fetch pending approvals
 * Returns selections and change orders ready for approval
 *
 * @returns {Object} Query result with approvals data
 */
export function usePendingApprovals() {
  return useQuery({
    queryKey: ['pendingApprovals'],
    queryFn: async () => {
      const getApprovals = httpsCallable(functions, 'getPendingApprovals');
      const result = await getApprovals();
      
      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to fetch approvals');
      }
      
      return result.data.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// CALENDAR / TASKS
// =============================================================================

/**
 * Hook to fetch all tasks for calendar view
 *
 * @param {Object} options - Filter options
 * @param {string} options.startDate - Start date filter (YYYY-MM-DD)
 * @param {string} options.endDate - End date filter (YYYY-MM-DD)
 * @returns {Object} Query result with tasks data
 */
export function useAllTasksForCalendar(options = {}) {
  const { startDate, endDate, showTestAccounts } = options;

  return useQuery({
    queryKey: ['calendarTasks', startDate, endDate, showTestAccounts],
    queryFn: async () => {
      const getTasks = httpsCallable(functions, 'getAllTasksForCalendar');
      const result = await getTasks({ startDate, endDate, showTestAccounts });
      
      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to fetch calendar tasks');
      }
      
      return result.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch tasks for a specific date
 *
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {Object} Query result with tasks data
 */
export function useTasksByDate(date) {
  return useQuery({
    queryKey: ['tasksByDate', date],
    queryFn: async () => {
      const getTasks = httpsCallable(functions, 'getTasksByDate');
      const result = await getTasks({ date });
      
      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to fetch tasks');
      }
      
      return result.data.data;
    },
    enabled: !!date, // Only run if date is provided
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =============================================================================
// COMBINED DASHBOARD DATA
// =============================================================================

/**
 * Hook to fetch all dashboard data in one call
 * Combines stats, alerts, deadlines, and approvals
 *
 * @returns {Object} Combined query results
 */
export function useAdminDashboard() {
  const statsQuery = useAdminDashboardStats();
  const alertsQuery = useAdminAlerts();
  const deadlinesQuery = useUpcomingDeadlines(30);
  const approvalsQuery = usePendingApprovals();
  
  return {
    stats: statsQuery.data,
    alerts: alertsQuery.data,
    deadlines: deadlinesQuery.data,
    approvals: approvalsQuery.data,
    isLoading: statsQuery.isLoading || alertsQuery.isLoading || 
               deadlinesQuery.isLoading || approvalsQuery.isLoading,
    isError: statsQuery.isError || alertsQuery.isError || 
             deadlinesQuery.isError || approvalsQuery.isError,
    error: statsQuery.error || alertsQuery.error || 
           deadlinesQuery.error || approvalsQuery.error,
    refetch: () => {
      statsQuery.refetch();
      alertsQuery.refetch();
      deadlinesQuery.refetch();
      approvalsQuery.refetch();
    },
  };
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  useAdminDashboardStats,
  useAdminAlerts,
  useUpcomingDeadlines,
  usePendingApprovals,
  useAllTasksForCalendar,
  useTasksByDate,
  useAdminDashboard,
};
